import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { roleLabel, roleColor, situacaoEleitLabel, situacaoEleitColor,
         validacaoWALabel, validacaoWAColor, limparTelefone, gerarMsgVerificacao } from '../utils/helpers';
import AppShell  from '../components/AppShell';
import KPICard   from '../components/KPICard';
import Pill      from '../components/Pill';
import TreeNode  from '../components/TreeNode';
import EditModal from '../components/EditModal';
import BotWhatsApp from '../components/BotWhatsApp';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
         PieChart, Pie, Cell, Legend } from 'recharts';

const TABS = [
  { id: 'dashboard', label: '📊 Dashboard'         },
  { id: 'pending',   label: '⏳ Pendentes'          },
  { id: 'active',    label: '✅ Usuários'           },
  { id: 'rede',      label: '🔺 Rede Hierárquica'  },
  { id: 'bot',       label: '🤖 Bot WhatsApp'       },
  { id: 'export',    label: '⬇️ Exportar CSV'       },
  { id: 'auxiliar',  label: '🛡 Auxiliar ADM'         },
];

const TOOLTIP_STYLE = { background: '#fff', border: '1px solid var(--primary)', borderRadius: 8, color: '#000' };

// ── Criar Auxiliar ADM ────────────────────────────────────────────────────────
function AuxiliarAdminTab({ onRefresh }) {
  const [form, setForm]   = useState({ nome:'', cpf:'', email:'', telefone:'', login:'', password:'' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const [error, setError]   = useState('');
  const salvar = async () => {
    if (!form.nome||!form.cpf||!form.email||!form.telefone||!form.login||!form.password)
      return setError('Preencha todos os campos');
    setSaving(true); setError(''); setMsg('');
    try {
      await axios.post('/api/users/auxiliar', form);
      setMsg('✅ Auxiliar ADM criado com sucesso!');
      setForm({ nome:'', cpf:'', email:'', telefone:'', login:'', password:'' });
      onRefresh();
    } catch (e) { setError(e.response?.data?.error || 'Erro ao criar'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <h2 className="page-title">🛡 Auxiliar Administrativo</h2>
      <div className="card" style={{ maxWidth:560 }}>
        <h3 className="card-title">➕ Criar Auxiliar ADM</h3>
        <p style={{ fontSize:13, color:'#555', margin:'8px 0 16px', lineHeight:1.6 }}>
          O Auxiliar ADM pode <strong>visualizar</strong> todos os usuários, <strong>alterar situação eleitoral</strong> (com registro de zona/seção), e usar o <strong>Bot WhatsApp</strong> e <strong>Pesquisa Eleitoral</strong>. Não pode editar dados cadastrais.
        </p>
        <div className="grid-2">
          <div className="field-group" style={{ gridColumn:'1/-1' }}>
            <label className="r-label">NOME COMPLETO</label>
            <input className="r-input" value={form.nome}
              onChange={e => setForm(f=>({...f, nome:e.target.value.toUpperCase()}))} required />
          </div>
          <div className="field-group">
            <label className="r-label">CPF</label>
            <input className="r-input" value={form.cpf}
              onChange={e => setForm(f=>({...f, cpf:e.target.value}))} placeholder="000.000.000-00" />
          </div>
          <div className="field-group">
            <label className="r-label">TELEFONE</label>
            <input className="r-input" value={form.telefone}
              onChange={e => setForm(f=>({...f, telefone:e.target.value}))} placeholder="(65) 99999-9999" />
          </div>
          <div className="field-group" style={{ gridColumn:'1/-1' }}>
            <label className="r-label">E-MAIL</label>
            <input className="r-input" type="email" value={form.email}
              onChange={e => setForm(f=>({...f, email:e.target.value}))} placeholder="email@exemplo.com" />
          </div>
          <div className="field-group">
            <label className="r-label">LOGIN DE ACESSO</label>
            <input className="r-input" value={form.login}
              onChange={e => setForm(f=>({...f, login:e.target.value.toLowerCase()}))} placeholder="login" />
          </div>
          <div className="field-group">
            <label className="r-label">SENHA</label>
            <input className="r-input" type="password" value={form.password}
              onChange={e => setForm(f=>({...f, password:e.target.value}))} placeholder="Mín. 6 caracteres" />
          </div>
        </div>
        {error && <div className="alert alert-danger" style={{ marginTop:12 }}>{error}</div>}
        {msg   && <div className="alert alert-success" style={{ marginTop:12 }}>{msg}</div>}
        <button onClick={salvar} disabled={saving} className="btn"
          style={{ marginTop:16, width:'100%', padding:13 }}>
          {saving ? 'Criando...' : '✅ Criar Auxiliar ADM'}
        </button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [users,       setUsers]       = useState([]);
  const [tree,        setTree]        = useState([]);
  const [tab,         setTab]         = useState('dashboard');
  const [loading,     setLoading]     = useState(true);
  const [inviteLink,  setInviteLink]  = useState('');

  // Filtros da tabela
  const [search,     setSearch]     = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterSit,  setFilterSit]  = useState('');
  const [filterMun,  setFilterMun]  = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uR, tR] = await Promise.all([
        axios.get('/api/users'),
        axios.get('/api/users/tree'),
      ]);
      setUsers(uR.data);
      setTree(tR.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const activate    = async (id)       => { await axios.patch(`/api/users/${id}/activate`); fetchAll(); };
  const changeRole  = async (id, role) => { await axios.patch(`/api/users/${id}/role`, { role }); fetchAll(); };
  const changeSitEl = async (id, val)  => { await axios.patch(`/api/users/${id}/situacao-eleitoral`, { situacao_eleitoral: val }); fetchAll(); };
  const getInviteLink = async () => {
    const r = await axios.get(`/api/users/${user.id}/invite-link`);
    setInviteLink(r.data.link);
  };

  // ── Dados derivados ──────────────────────────────────────────────────────
  const nonAdmin  = users.filter(u => u.role !== 'ADMIN');
  const pending   = nonAdmin.filter(u => u.situacao_cadastro === 'PENDENTE');
  const active    = nonAdmin.filter(u => u.situacao_cadastro === 'ATIVO');
  const uniqueMun = [...new Set(nonAdmin.map(u => u.municipio).filter(Boolean))].sort();
  const coordsGerais = users.filter(u => u.role === 'COORDENADOR_GERAL');

  const stats = {
    total:         nonAdmin.length,
    pendentes:     pending.length,
    ativos:        active.length,
    emAnalise:     nonAdmin.filter(u => u.situacao_eleitoral === 'EM_ANALISE').length,
    irregular:     nonAdmin.filter(u => u.situacao_eleitoral === 'IRREGULAR').length,
    elAtivo:       nonAdmin.filter(u => u.situacao_eleitoral === 'ATIVO').length,
    coordGeral:    nonAdmin.filter(u => u.role === 'COORDENADOR_GERAL').length,
    coordMun:      nonAdmin.filter(u => u.role === 'COORDENADOR_MUNICIPAL').length,
    lideranca:     nonAdmin.filter(u => u.role === 'LIDERANCA').length,
    apoiador:      nonAdmin.filter(u => u.role === 'APOIADOR').length,
    waAtivo:       nonAdmin.filter(u => u.validacao_whatsapp === 'ATIVO').length,
    waNaoValidado: nonAdmin.filter(u => u.validacao_whatsapp === 'NAO_VALIDADO').length,
    waInativo:     nonAdmin.filter(u => !u.validacao_whatsapp || u.validacao_whatsapp === 'INATIVO').length,
  };

  const porMun = uniqueMun
    .map(m => ({ municipio: m, total: nonAdmin.filter(u => u.municipio === m).length }))
    .sort((a, b) => b.total - a.total).slice(0, 15);

  const porRoleData = [
    { name: 'Coord. Geral',     value: stats.coordGeral, color: '#1a56db' },
    { name: 'Coord. Municipal', value: stats.coordMun,   color: '#057a55' },
    { name: 'Liderança',        value: stats.lideranca,  color: '#c27803' },
    { name: 'Apoiador',         value: stats.apoiador,   color: '#6b7280' },
  ].filter(d => d.value > 0);

  const porSitData = [
    { name: 'Em Análise', value: stats.emAnalise, color: '#c27803' },
    { name: 'Irregular',  value: stats.irregular,  color: '#cc0000' },
    { name: 'Ativo',      value: stats.elAtivo,    color: '#057a55' },
  ].filter(d => d.value > 0);

  const applyFilters = (list) => list.filter(u =>
    (!search     || (u.nome||'').includes(search.toUpperCase()) || (u.login||'').includes(search) || (u.municipio||'').includes(search.toUpperCase())) &&
    (!filterRole || u.role === filterRole) &&
    (!filterSit  || u.situacao_eleitoral === filterSit) &&
    (!filterMun  || u.municipio === filterMun)
  );

  const pendingFiltered = applyFilters(pending);
  const activeFiltered  = applyFilters(active);

  const sideBottom = (
    <>
      <button onClick={getInviteLink} className="btn btn-light btn-sm" style={{ width: '100%' }}>
        🔗 Meu Link de Convite
      </button>
      {inviteLink && (
        <div className="invite-box">
          <input readOnly value={inviteLink} className="invite-input"
            onClick={e => { e.target.select(); navigator.clipboard.writeText(inviteLink); }} />
          <button onClick={() => navigator.clipboard.writeText(inviteLink)} className="copy-btn">📋</button>
        </div>
      )}
      <button onClick={logout} className="btn btn-outline btn-sm"
        style={{ width: '100%', borderColor: 'var(--danger)', color: 'var(--danger)' }}>Sair</button>
    </>
  );

  return (
    <AppShell
      tabs={TABS.map(t => ({ ...t, label: t.label + (t.id === 'pending' && pending.length ? ` (${pending.length})` : '') }))}
      activeTab={tab}
      onTabChange={setTab}
      roleBadge="👑 ADMINISTRADOR"
      userName={user?.nome || user?.login}
      sideBottom={sideBottom}
    >
      {loading && <div className="loading-screen"><div className="spinner" /> Carregando...</div>}

      {/* ── DASHBOARD ── */}
      {!loading && tab === 'dashboard' && (
        <div>
          <h2 className="page-title">Dashboard Gerencial</h2>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 8 }}>CADASTROS</div>
          <div className="kpi-grid" style={{ marginBottom: 14 }}>
            <KPICard label="Total Cadastros"   value={stats.total}     icon="👥" color="#000080" />
            <KPICard label="Pendentes"          value={stats.pendentes} icon="⏳" color="#c27803" />
            <KPICard label="Ativos"             value={stats.ativos}    icon="✅" color="#057a55" />
            <KPICard label="Coord. Gerais"      value={stats.coordGeral} icon="🔵" color="#1a56db" />
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 8 }}>SITUAÇÃO ELEITORAL</div>
          <div className="kpi-grid" style={{ marginBottom: 14 }}>
            <KPICard label="Em Análise"         value={stats.emAnalise} icon="🔍" color="#c27803" />
            <KPICard label="Irregular"          value={stats.irregular} icon="⚠️" color="#cc0000" />
            <KPICard label="Ativo Eleitoralmente" value={stats.elAtivo} icon="🗳️" color="#057a55" />
            <KPICard label="Coord. Municipais"  value={stats.coordMun}  icon="🟢" color="#057a55" />
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--primary)', letterSpacing: 1, marginBottom: 8 }}>WHATSAPP</div>
          <div className="kpi-grid" style={{ marginBottom: 20 }}>
            <KPICard label="WA Validados"     value={stats.waAtivo}       icon="✅" color="#057a55" />
            <KPICard label="WA Não Validados" value={stats.waNaoValidado} icon="📵" color="#cc0000" />
            <KPICard label="WA Pendente"      value={stats.waInativo}     icon="⬜" color="#6b7280" />
            <KPICard label="Taxa Validação"
              value={stats.total > 0 ? Math.round((stats.waAtivo / stats.total) * 100) + '%' : '0%'}
              icon="📊" color="#1a56db" />
          </div>

          <div className="charts-grid">
            <div className="card">
              <h3 className="card-title">Distribuição por Perfil</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={porRoleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                    label={({ name, value }) => `${name}: ${value}`}>
                    {porRoleData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="card">
              <h3 className="card-title">Situação Eleitoral</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={porSitData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75}
                    label={({ name, value }) => `${name}: ${value}`}>
                    {porSitData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Cadastros por Município</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={porMun}>
                <XAxis dataKey="municipio" stroke="var(--primary)" tick={{ fontSize: 11 }} />
                <YAxis stroke="var(--primary)" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="total" fill="var(--primary)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <h3 className="card-title">Resumo por Município</h3>
            <div className="table-wrap">
              <table className="r-table">
                <thead>
                  <tr>
                    {['MUNICÍPIO','TOTAL','COORD. MUN.','LIDERANÇAS','APOIADORES','EM ANÁLISE','IRREGULAR','ATIVO'].map(h =>
                      <th key={h}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {porMun.map(({ municipio }) => {
                    const mu = nonAdmin.filter(u => u.municipio === municipio);
                    const cell = (v, color) => <td style={{ textAlign: 'center', color, fontWeight: color ? 600 : 400 }}>{v}</td>;
                    return (
                      <tr key={municipio}>
                        <td><strong>{municipio}</strong></td>
                        {cell(mu.length, 'var(--primary)')}
                        {cell(mu.filter(u => u.role === 'COORDENADOR_MUNICIPAL').length)}
                        {cell(mu.filter(u => u.role === 'LIDERANCA').length)}
                        {cell(mu.filter(u => u.role === 'APOIADOR').length)}
                        {cell(mu.filter(u => u.situacao_eleitoral === 'EM_ANALISE').length, '#c27803')}
                        {cell(mu.filter(u => u.situacao_eleitoral === 'IRREGULAR').length,  '#cc0000')}
                        {cell(mu.filter(u => u.situacao_eleitoral === 'ATIVO').length,      '#057a55')}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── PENDENTES ── */}
      {!loading && tab === 'pending' && (
        <UserTable title="Cadastros Pendentes" list={pendingFiltered}
          search={search} setSearch={setSearch}
          filterRole={filterRole} setFilterRole={setFilterRole}
          filterSit={filterSit} setFilterSit={setFilterSit}
          filterMun={filterMun} setFilterMun={setFilterMun}
          uniqueMun={uniqueMun} showActivate
          onActivate={activate} onChangeRole={changeRole}
          onChangeSitEl={changeSitEl} onRefresh={fetchAll} />
      )}

      {/* ── USUÁRIOS ATIVOS ── */}
      {!loading && tab === 'active' && (
        <UserTable title="Usuários Ativos" list={activeFiltered}
          search={search} setSearch={setSearch}
          filterRole={filterRole} setFilterRole={setFilterRole}
          filterSit={filterSit} setFilterSit={setFilterSit}
          filterMun={filterMun} setFilterMun={setFilterMun}
          uniqueMun={uniqueMun}
          onChangeRole={changeRole} onChangeSitEl={changeSitEl} onRefresh={fetchAll} />
      )}

      {/* ── REDE HIERÁRQUICA ── */}
      {!loading && tab === 'rede' && (
        <div>
          <h2 className="page-title">Rede Hierárquica</h2>
          <p className="page-subtitle">Clique em ▶ para expandir. Use 📲 WhatsApp para verificar diretamente com o colaborador.</p>
          <div className="tree-container">
            {tree.map(node => <TreeNode key={node.id} node={node} level={0} />)}
          </div>
        </div>
      )}

      {/* ── BOT WHATSAPP ── */}
      {tab === 'bot' && (
        <BotWhatsApp users={users} coordId={null} coordsGerais={coordsGerais} />
      )}

      {/* ── AUXILIAR ADM ── */}
      {!loading && tab === 'auxiliar' && (
        <AuxiliarAdminTab onRefresh={fetchAll} />
      )}

      {/* ── EXPORTAR CSV ── */}
      {!loading && tab === 'export' && (
        <ExportCSV users={nonAdmin} uniqueMun={uniqueMun} coordsGerais={coordsGerais} />
      )}
    </AppShell>
  );
}

// ── USER TABLE ────────────────────────────────────────────────────────────────
function UserTable({ title, list, search, setSearch, filterRole, setFilterRole,
  filterSit, setFilterSit, filterMun, setFilterMun, uniqueMun,
  showActivate, onActivate, onChangeRole, onChangeSitEl, onRefresh }) {

  const [editUser, setEditUser] = useState(null);

  return (
    <div>
      <h2 className="page-title">{title} <span style={{ fontSize: 15, color: 'var(--primary)' }}>({list.length})</span></h2>

      {editUser && (
        <EditModal user={editUser} onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); onRefresh?.(); }} />
      )}

      <div className="filter-bar">
        <input className="r-input" placeholder="Buscar nome, login, município..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="r-select" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="">Todos perfis</option>
          <option value="COORDENADOR_GERAL">Coord. Geral</option>
          <option value="COORDENADOR_MUNICIPAL">Coord. Municipal</option>
          <option value="LIDERANCA">Liderança</option>
          <option value="APOIADOR">Apoiador</option>
        </select>
        <select className="r-select" value={filterSit} onChange={e => setFilterSit(e.target.value)}>
          <option value="">Situação Eleitoral</option>
          <option value="EM_ANALISE">Em Análise</option>
          <option value="IRREGULAR">Irregular</option>
          <option value="ATIVO">Ativo</option>
        </select>
        <select className="r-select" value={filterMun} onChange={e => setFilterMun(e.target.value)}>
          <option value="">Município</option>
          {uniqueMun.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="table-wrap">
        <table className="r-table">
          <thead>
            <tr>
              {['NOME / LOGIN','CPF','MUNICÍPIO','TIPO','PERFIL','SIT. ELEITORAL','CADASTRO','VALID. WA','WHATSAPP','AÇÕES'].map(h =>
                <th key={h}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {list.length === 0
              ? <tr><td colSpan={10} style={{ textAlign: 'center', color: '#666', padding: 40 }}>Nenhum resultado encontrado</td></tr>
              : list.map(u => (
                <tr key={u.id}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{u.nome || '-'}</div>
                    <div style={{ fontSize: 11, color: '#555' }}>{u.login}</div>
                  </td>
                  <td>{u.cpf  || '-'}</td>
                  <td>{u.municipio || '-'}</td>
                  <td>{u.tipo || '-'}</td>
                  <td><Pill label={roleLabel(u.role)} color={roleColor(u.role)} /></td>
                  <td><Pill label={situacaoEleitLabel(u.situacao_eleitoral)} color={situacaoEleitColor(u.situacao_eleitoral)} /></td>
                  <td>
                    <Pill
                      label={u.situacao_cadastro === 'ATIVO' ? '✅ Ativo' : '⏳ Pendente'}
                      color={u.situacao_cadastro === 'ATIVO' ? '#057a55' : '#c27803'} />
                  </td>
                  <td><Pill label={validacaoWALabel(u.validacao_whatsapp)} color={validacaoWAColor(u.validacao_whatsapp)} /></td>
                  <td><WABtn u={u} onChangeSitEl={onChangeSitEl} /></td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      {showActivate && u.situacao_cadastro === 'PENDENTE' && (
                        <button onClick={() => onActivate(u.id)} className="btn btn-sm btn-success">✅ Ativar</button>
                      )}
                      <button onClick={() => setEditUser(u)} className="btn btn-sm btn-light">✏️ Editar</button>
                      <select onChange={e => { if (e.target.value) { onChangeRole(u.id, e.target.value); e.target.value = ''; } }}
                        className="r-select" style={{ fontSize: 11, padding: '4px 6px' }} defaultValue="">
                        <option value="" disabled>Perfil...</option>
                        <option value="COORDENADOR_GERAL">Coord. Geral</option>
                        <option value="COORDENADOR_MUNICIPAL">Coord. Municipal</option>
                        <option value="LIDERANCA">Liderança</option>
                        <option value="APOIADOR">Apoiador</option>
                      </select>
                      <select onChange={e => { if (e.target.value) { onChangeSitEl(u.id, e.target.value); e.target.value = ''; } }}
                        className="r-select" style={{ fontSize: 11, padding: '4px 6px' }} defaultValue="">
                        <option value="" disabled>Sit. Eleitoral...</option>
                        <option value="EM_ANALISE">Em Análise</option>
                        <option value="IRREGULAR">Irregular</option>
                        <option value="ATIVO">Ativo</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── WHATSAPP BUTTON ───────────────────────────────────────────────────────────
function WABtn({ u, onChangeSitEl }) {
  const [showModal, setShowModal] = useState(false);
  const [resp,      setResp]      = useState('');

  if (!u.telefone) return <span style={{ color: '#aaa', fontSize: 11 }}>Sem telefone</span>;

  const tel   = limparTelefone(u.telefone);
  const waUrl = `https://wa.me/55${tel}?text=${encodeURIComponent(gerarMsgVerificacao(u.nome || u.login, u.cpf))}`;

  const confirmar = () => {
    if (!resp) return alert('Selecione uma resposta.');
    // Bug corrigido: resp '0' → NAO_VALIDADO (não IRREGULAR)
    onChangeSitEl(u.id, resp === '1' ? 'ATIVO' : 'NAO_VALIDADO');
    setShowModal(false); setResp('');
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <a href={waUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-wa" style={{ fontSize: 11 }}>
          📲 Enviar
        </a>
        <button onClick={() => setShowModal(true)} className="btn btn-sm btn-light" style={{ fontSize: 11 }}>
          📋 Registrar
        </button>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">📋 Registrar Resposta</h3>
              <button className="modal-close" onClick={() => { setShowModal(false); setResp(''); }}>✕</button>
            </div>
            <p style={{ fontSize: 13, marginBottom: 16 }}>
              <strong>{u.nome}</strong> — CPF: {u.cpf} — Tel: {u.telefone}
            </p>
            <label className="r-label">RESPOSTA DO COLABORADOR</label>
            <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
              <button onClick={() => setResp('1')} className="btn" style={{ flex: 1, background: resp === '1' ? '#057a55' : '#f0f8ff', color: resp === '1' ? '#fff' : '#057a55', border: '2px solid #057a55' }}>
                1 — ✅ Confirmou
              </button>
              <button onClick={() => setResp('0')} className="btn" style={{ flex: 1, background: resp === '0' ? '#cc0000' : '#f0f8ff', color: resp === '0' ? '#fff' : '#cc0000', border: '2px solid #cc0000' }}>
                0 — ❌ Não confirmou
              </button>
            </div>
            {resp === '1' && <div className="alert alert-success">Validação WA → <strong>VALIDADO</strong></div>}
            {resp === '0' && <div className="alert alert-danger">Validação WA → <strong>NÃO VALIDADO</strong></div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
              <button onClick={confirmar} disabled={!resp} className="btn" style={{ flex: 1, opacity: resp ? 1 : 0.5 }}>Confirmar</button>
              <button onClick={() => { setShowModal(false); setResp(''); }} className="btn btn-outline" style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── EXPORT CSV ────────────────────────────────────────────────────────────────
const ALL_COLUMNS = [
  // Identificação
  { key: 'nome',                label: 'Nome',                  group: 'Identificação'    },
  { key: 'login',               label: 'Login',                 group: 'Identificação'    },
  { key: 'cpf',                 label: 'CPF',                   group: 'Identificação'    },
  { key: 'telefone',            label: 'Telefone',              group: 'Identificação'    },
  { key: 'municipio',           label: 'Município',             group: 'Identificação'    },
  { key: 'tipo',                label: 'Tipo Eleitoral',        group: 'Identificação'    },
  { key: 'created_at',          label: 'Data de Cadastro',      group: 'Identificação'    },
  // Status
  { key: 'role',                label: 'Perfil',                group: 'Status'           },
  { key: 'situacao_cadastro',   label: 'Situação Cadastro',     group: 'Status'           },
  { key: 'situacao_eleitoral',  label: 'Situação Eleitoral',    group: 'Status'           },
  { key: 'validacao_whatsapp',  label: 'Validação WhatsApp',    group: 'Status'           },
  // Rede
  { key: '_coord_geral',        label: 'Coordenador Geral',     group: 'Rede'             },
  { key: '_coord_municipal',    label: 'Coord. Municipal',      group: 'Rede'             },
  { key: '_lideranca',          label: 'Liderança Superior',    group: 'Rede'             },
  { key: '_responsavel_direto', label: 'Responsável Direto',    group: 'Rede'             },
  { key: '_nivel_rede',         label: 'Nível na Rede',         group: 'Rede'             },
];

const COL_GROUPS = ['Identificação', 'Status', 'Rede'];

function ExportCSV({ users, uniqueMun, coordsGerais }) {
  const [fRole,      setFRole]      = useState('');
  const [fSitEl,     setFSitEl]     = useState('');
  const [fSitCad,    setFSitCad]    = useState('');
  const [fWA,        setFWA]        = useState('');
  const [fMun,       setFMun]       = useState('');
  const [fCoord,     setFCoord]     = useState('');
  const [cols,       setCols]       = useState(ALL_COLUMNS.map(c => c.key));
  const [downloaded, setDownloaded] = useState(false);

  const ROLE_MAP    = { COORDENADOR_GERAL: 'Coord. Geral', COORDENADOR_MUNICIPAL: 'Coord. Municipal', LIDERANCA: 'Liderança', APOIADOR: 'Apoiador' };
  const SIT_EL_MAP  = { EM_ANALISE: 'Em Análise', IRREGULAR: 'Irregular', ATIVO: 'Ativo' };
  const WA_MAP      = { ATIVO: 'Validado', NAO_VALIDADO: 'Não Validado', INATIVO: 'Pendente' };
  const NIVEL_MAP   = { COORDENADOR_GERAL: '1 – Coord. Geral', COORDENADOR_MUNICIPAL: '2 – Coord. Municipal', LIDERANCA: '3 – Liderança', APOIADOR: '4 – Apoiador' };

  // Mapa id → usuário para lookup rápido
  const userMap = Object.fromEntries(users.map(u => [u.id, u]));

  // Sobe na árvore buscando o primeiro ancestral com o role alvo
  const findAncestor = (u, targetRole) => {
    let cur = userMap[u.parent_id];
    while (cur) {
      if (cur.role === targetRole) return cur.nome || cur.login;
      cur = userMap[cur.parent_id];
    }
    return '';
  };

  // Enriquece cada usuário com campos de rede calculados
  const enrich = (u) => ({
    ...u,
    _coord_geral:        u.role === 'COORDENADOR_GERAL' ? (u.nome || u.login) : findAncestor(u, 'COORDENADOR_GERAL'),
    _coord_municipal:    u.role === 'COORDENADOR_MUNICIPAL' ? (u.nome || u.login) : findAncestor(u, 'COORDENADOR_MUNICIPAL'),
    _lideranca:          u.role === 'LIDERANCA' ? (u.nome || u.login) : findAncestor(u, 'LIDERANCA'),
    _responsavel_direto: u.parent_id ? (userMap[u.parent_id]?.nome || userMap[u.parent_id]?.login || '') : '',
    _nivel_rede:         NIVEL_MAP[u.role] || u.role,
  });

  // BFS descendentes
  const getSubtree = (parentId) => {
    const result = []; const queue = [parentId];
    while (queue.length) {
      const pid = queue.shift();
      users.filter(u => u.parent_id === pid).forEach(c => { result.push(c.id); queue.push(c.id); });
    }
    return result;
  };

  const enrichedUsers = users.map(enrich);

  const filtered = enrichedUsers.filter(u => {
    if (fRole   && u.role                              !== fRole)   return false;
    if (fSitEl  && u.situacao_eleitoral                !== fSitEl)  return false;
    if (fSitCad && u.situacao_cadastro                 !== fSitCad) return false;
    if (fWA     && (u.validacao_whatsapp || 'INATIVO') !== fWA)     return false;
    if (fMun    && u.municipio                         !== fMun)    return false;
    if (fCoord  && !getSubtree(fCoord).includes(u.id))              return false;
    return true;
  });

  // Ordena: por coord. geral → coord. municipal → perfil → nome
  const sorted = [...filtered].sort((a, b) => {
    const cg = (a._coord_geral || '').localeCompare(b._coord_geral || '');
    if (cg !== 0) return cg;
    const cm = (a._coord_municipal || '').localeCompare(b._coord_municipal || '');
    if (cm !== 0) return cm;
    const rl = (a.role || '').localeCompare(b.role || '');
    if (rl !== 0) return rl;
    return (a.nome || '').localeCompare(b.nome || '');
  });

  const toggleCol    = (key) => setCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  const toggleGroup  = (grp) => {
    const keys = ALL_COLUMNS.filter(c => c.group === grp).map(c => c.key);
    const allOn = keys.every(k => cols.includes(k));
    setCols(prev => allOn ? prev.filter(k => !keys.includes(k)) : [...new Set([...prev, ...keys])]);
  };
  const toggleAll = () => setCols(cols.length === ALL_COLUMNS.length ? [] : ALL_COLUMNS.map(c => c.key));

  const formatVal = (u, key) => {
    const v = u[key] ?? '';
    if (key === 'role')               return ROLE_MAP[v]   || v;
    if (key === 'situacao_eleitoral') return SIT_EL_MAP[v] || v;
    if (key === 'validacao_whatsapp') return WA_MAP[v]     || v;
    if (key === 'created_at' && v)    return new Date(v).toLocaleDateString('pt-BR');
    return String(v);
  };

  const downloadCSV = () => {
    if (!cols.length)    return alert('Selecione ao menos uma coluna.');
    if (!sorted.length)  return alert('Nenhum registro com os filtros aplicados.');
    const selectedCols = ALL_COLUMNS.filter(c => cols.includes(c.key));
    const header = selectedCols.map(c => `"${c.label}"`).join(';');
    const rows   = sorted.map(u => selectedCols.map(c => `"${formatVal(u, c.key)}"`).join(';'));
    const csv    = '\uFEFF' + [header, ...rows].join('\r\n');
    const blob   = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement('a');
    a.href = url;
    a.download = `politics-system-${fCoord ? (coordsGerais.find(c=>c.id===fCoord)?.nome||'rede') + '-' : ''}${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    setDownloaded(true); setTimeout(() => setDownloaded(false), 3000);
  };

  const clearFilters = () => { setFRole(''); setFSitEl(''); setFSitCad(''); setFWA(''); setFMun(''); setFCoord(''); };

  const S = {
    wrap:      { maxWidth: 920 },
    title:     { fontSize: 22, fontWeight: 700, color: 'var(--primary)', marginBottom: 4 },
    sub:       { fontSize: 13, color: '#666', marginBottom: 28 },
    card:      { background: '#fff', border: '1px solid var(--border)', borderRadius: 12, padding: '22px 24px', marginBottom: 20 },
    cardTitle: { fontSize: 12, fontWeight: 700, letterSpacing: 1, color: 'var(--primary)', textTransform: 'uppercase', marginBottom: 14 },
    grid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 },
    label:     { fontSize: 11, fontWeight: 600, color: '#555', letterSpacing: 0.5, display: 'block', marginBottom: 4 },
    colGroup:  { marginBottom: 16 },
    colGroupTitle: { fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#888', textTransform: 'uppercase',
                     marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' },
    colGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 },
    colItem:   { display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 },
    preview:   { background: 'var(--bg)', border: '1.5px solid var(--primary)', borderRadius: 10,
                 padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                 flexWrap: 'wrap', gap: 12, marginBottom: 20 },
    count:     { fontSize: 28, fontWeight: 800, color: 'var(--primary)' },
    countSub:  { fontSize: 12, color: '#666' },
  };

  // Resumo por rede para exibir no painel
  const resumoRede = coordsGerais.map(cg => {
    const sub = getSubtree(cg.id);
    const membros = filtered.filter(u => sub.includes(u.id));
    return { nome: cg.nome || cg.login, total: membros.length };
  }).sort((a,b) => b.total - a.total);

  return (
    <div style={S.wrap}>
      <h2 style={S.title}>⬇️ Exportar Dados CSV</h2>
      <p style={S.sub}>Aplique filtros, selecione as colunas e faça o download. O CSV inclui os dados de rede de cada colaborador.</p>

      {/* FILTROS */}
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={S.cardTitle}>🔍 Filtros</div>
          <button onClick={clearFilters} className="btn btn-sm btn-light" style={{ fontSize: 12 }}>✕ Limpar</button>
        </div>
        <div style={S.grid}>
          <div>
            <label style={S.label}>PERFIL</label>
            <select className="r-select" value={fRole} onChange={e => setFRole(e.target.value)}>
              <option value="">Todos</option>
              <option value="COORDENADOR_GERAL">Coord. Geral</option>
              <option value="COORDENADOR_MUNICIPAL">Coord. Municipal</option>
              <option value="LIDERANCA">Liderança</option>
              <option value="APOIADOR">Apoiador</option>
            </select>
          </div>
          <div>
            <label style={S.label}>SITUAÇÃO ELEITORAL</label>
            <select className="r-select" value={fSitEl} onChange={e => setFSitEl(e.target.value)}>
              <option value="">Todas</option>
              <option value="EM_ANALISE">Em Análise</option>
              <option value="IRREGULAR">Irregular</option>
              <option value="ATIVO">Ativo</option>
            </select>
          </div>
          <div>
            <label style={S.label}>SITUAÇÃO CADASTRO</label>
            <select className="r-select" value={fSitCad} onChange={e => setFSitCad(e.target.value)}>
              <option value="">Todas</option>
              <option value="ATIVO">Ativo</option>
              <option value="PENDENTE">Pendente</option>
            </select>
          </div>
          <div>
            <label style={S.label}>VALIDAÇÃO WHATSAPP</label>
            <select className="r-select" value={fWA} onChange={e => setFWA(e.target.value)}>
              <option value="">Todas</option>
              <option value="ATIVO">Validado</option>
              <option value="NAO_VALIDADO">Não Validado</option>
              <option value="INATIVO">Pendente</option>
            </select>
          </div>
          <div>
            <label style={S.label}>MUNICÍPIO</label>
            <select className="r-select" value={fMun} onChange={e => setFMun(e.target.value)}>
              <option value="">Todos</option>
              {uniqueMun.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>REDE DO COORDENADOR GERAL</label>
            <select className="r-select" value={fCoord} onChange={e => setFCoord(e.target.value)}>
              <option value="">Todas as redes</option>
              {coordsGerais.map(c => <option key={c.id} value={c.id}>{c.nome || c.login}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* RESUMO POR REDE */}
      {resumoRede.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>🔺 Distribuição por Rede ({filtered.length} registros filtrados)</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
            {resumoRede.map(r => (
              <div key={r.nome} style={{ background: r.total === 0 ? '#fafafa' : 'var(--bg)',
                border:'1px solid var(--border)',
                borderLeft: `4px solid ${r.total === 0 ? '#d1d5db' : 'var(--primary)'}`,
                borderRadius:8, padding:'10px 16px', minWidth:160,
                opacity: r.total === 0 ? 0.6 : 1 }}>
                <div style={{ fontSize:11, color:'#666', marginBottom:2 }}>Rede de</div>
                <div style={{ fontWeight:700, fontSize:13, color: r.total === 0 ? '#9ca3af' : 'var(--primary)' }}>{r.nome}</div>
                <div style={{ fontSize:20, fontWeight:800, color: r.total === 0 ? '#9ca3af' : 'var(--primary)', marginTop:4 }}>
                  {r.total}
                  <span style={{ fontSize:12, fontWeight:400, color:'#666' }}> membros</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* COLUNAS */}
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={S.cardTitle}>📋 Colunas a exportar</div>
          <button onClick={toggleAll} className="btn btn-sm btn-light" style={{ fontSize: 12 }}>
            {cols.length === ALL_COLUMNS.length ? '☐ Desmarcar todas' : '☑ Selecionar todas'}
          </button>
        </div>
        {COL_GROUPS.map(grp => {
          const grpCols = ALL_COLUMNS.filter(c => c.group === grp);
          const allOn   = grpCols.every(c => cols.includes(c.key));
          const grpColors = { 'Identificação':'#1a56db', 'Status':'#057a55', 'Rede':'#7e3af2' };
          return (
            <div key={grp} style={S.colGroup}>
              <div style={{ ...S.colGroupTitle }} onClick={() => toggleGroup(grp)}>
                <input type="checkbox" checked={allOn} readOnly
                  style={{ accentColor: grpColors[grp], width:14, height:14 }} />
                <span style={{ color: grpColors[grp] }}>● {grp}</span>
              </div>
              <div style={{ ...S.colGrid, paddingLeft:22 }}>
                {grpCols.map(c => (
                  <label key={c.key} style={S.colItem}>
                    <input type="checkbox" checked={cols.includes(c.key)} onChange={() => toggleCol(c.key)}
                      style={{ accentColor: grpColors[grp], width:14, height:14 }} />
                    <span style={{ color: cols.includes(c.key) ? grpColors[grp] : '#aaa', fontSize:12 }}>{c.label}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* PREVIEW + DOWNLOAD */}
      <div style={S.preview}>
        <div>
          <div style={S.count}>{sorted.length} <span style={{ fontSize:14, fontWeight:400 }}>registros</span></div>
          <div style={S.countSub}>{cols.length} coluna{cols.length!==1?'s':''} · ordenado por rede → município → nome</div>
        </div>
        <div style={{ textAlign:'right' }}>
          {sorted.length === 0 && <div style={{ fontSize:12, color:'#cc0000', marginBottom:8 }}>⚠️ Nenhum registro.</div>}
          {cols.length   === 0 && <div style={{ fontSize:12, color:'#cc0000', marginBottom:8 }}>⚠️ Selecione ao menos uma coluna.</div>}
          <button onClick={downloadCSV} disabled={!sorted.length || !cols.length} className="btn"
            style={{ padding:'12px 28px', fontSize:14, fontWeight:700,
              opacity: (!sorted.length || !cols.length) ? 0.4 : 1,
              background: downloaded ? '#057a55' : 'var(--primary)', transition:'background 0.3s' }}>
            {downloaded ? '✅ Download concluído!' : '⬇️ Baixar CSV'}
          </button>
        </div>
      </div>

      {/* MINI PREVIEW */}
      {sorted.length > 0 && cols.length > 0 && (
        <div style={S.card}>
          <div style={S.cardTitle}>👁 Prévia — primeiros {Math.min(8, sorted.length)} registros</div>
          <div className="table-wrap">
            <table className="r-table" style={{ fontSize:11 }}>
              <thead>
                <tr>
                  {ALL_COLUMNS.filter(c => cols.includes(c.key)).map(c => <th key={c.key}>{c.label.toUpperCase()}</th>)}
                </tr>
              </thead>
              <tbody>
                {sorted.slice(0, 8).map((u, i) => (
                  <tr key={i}>
                    {ALL_COLUMNS.filter(c => cols.includes(c.key)).map(c => (
                      <td key={c.key} style={c.group === 'Rede' ? { background:'#f5f0ff', color:'#5521b5', fontSize:11 } : {}}>
                        {formatVal(u, c.key) || <span style={{ color:'#ccc' }}>—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sorted.length > 8 && (
            <div style={{ fontSize:12, color:'#888', marginTop:10, textAlign:'center' }}>
              … e mais {sorted.length - 8} registro{sorted.length-8!==1?'s':''} no arquivo completo.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
